// Custom hook for Image Editor state and logic
import { useState, useCallback, useRef, useEffect } from 'react';
// import { useToast } from '@chakra-ui/react';
import { Crop, PixelCrop, centerCrop, makeAspectCrop } from 'react-image-crop';
import { ImageItem, OutputFormat } from '../types';
import useCustomToast from '@/components/common/Toast';

export const useImageEditor = () => {
  // const toast = useToast();
  const { showToast } = useCustomToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  // Images state
  const [images, setImages] = useState<ImageItem[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  // Editor settings
  const [width, setWidth] = useState<number>(0);
  const [height, setHeight] = useState<number>(0);
  const [aspectLocked, setAspectLocked] = useState(true);
  const [aspectRatio, setAspectRatio] = useState<number>(1);
  const [quality, setQuality] = useState<number>(80);
  const [outputFormat, setOutputFormat] = useState<OutputFormat>('jpeg');

  // Crop state
  const [isCropping, setIsCropping] = useState(false);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [cropAspect, setCropAspect] = useState<number | undefined>(undefined);

  // Processing state
  const [isProcessing, setIsProcessing] = useState(false);

  // Crop action loading states
  const [isApplyingCrop, setIsApplyingCrop] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  // Track last processed settings to enable/disable process button
  const [lastProcessedSettings, setLastProcessedSettings] = useState<{
    width: number;
    height: number;
    quality: number;
    format: OutputFormat;
    imageId: string;
  } | null>(null);

  const currentImage = images[currentIndex];

  // Cleanup URLs on unmount
  useEffect(() => {
    return () => {
      images.forEach((img) => {
        URL.revokeObjectURL(img.originalUrl);
        if (img.processedUrl) URL.revokeObjectURL(img.processedUrl);
      });
    };
  }, []);

  // Keyboard shortcut for download (Ctrl/Cmd + D)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'd') {
        e.preventDefault();
        if (currentImage?.processedUrl) {
          const link = document.createElement('a');
          link.href = currentImage.processedUrl;
          const baseName = currentImage.file.name.replace(/\.[^/.]+$/, '');
          link.download = `${baseName}_processed.${outputFormat}`;
          link.click();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentImage?.processedUrl, currentImage?.file.name, outputFormat]);

  // Update dimensions when switching images
  useEffect(() => {
    if (currentImage) {
      setWidth(currentImage.originalWidth);
      setHeight(currentImage.originalHeight);
      setAspectRatio(currentImage.originalWidth / currentImage.originalHeight);
      setIsCropping(false);
      setCrop(undefined);
      setCompletedCrop(undefined);
    }
  }, [currentIndex, currentImage?.id]);

  const handleFilesSelect = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return;

      const validFiles = Array.from(files).filter((file) =>
        file.type.match(/image\/(jpeg|jpg|png|webp|gif)/i),
      );

      if (validFiles.length === 0) {
        // toast({ title: 'No valid image files selected', status: 'warning' });
        showToast('warning', 'No valid image files selected');
        return;
      }

      let loadedCount = 0;
      const newImages: ImageItem[] = [];

      validFiles.forEach((file) => {
        const url = URL.createObjectURL(file);
        // Create a separate URL for pristine copy to avoid sharing the same blob URL
        const pristineUrl = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
          newImages.push({
            id: Math.random().toString(36).substr(2, 9),
            file,
            originalUrl: url,
            originalWidth: img.naturalWidth,
            originalHeight: img.naturalHeight,
            // Store pristine copy with its own separate URL
            pristineUrl: pristineUrl,
            pristineWidth: img.naturalWidth,
            pristineHeight: img.naturalHeight,
            pristineFile: file,
          });
          loadedCount++;

          if (loadedCount === validFiles.length) {
            setImages((prev) => {
              const updatedImages = [...prev, ...newImages];
              if (prev.length === 0 && newImages.length > 0) {
                setWidth(newImages[0].originalWidth);
                setHeight(newImages[0].originalHeight);
                setAspectRatio(newImages[0].originalWidth / newImages[0].originalHeight);
              }
              return updatedImages;
            });
          }
        };
        img.onerror = () => {
          URL.revokeObjectURL(url);
          loadedCount++;
          // toast({ title: `Failed to load ${file.name}`, status: 'error' });
          showToast('error', `Failed to load ${file.name}`);

          if (loadedCount === validFiles.length && newImages.length > 0) {
            setImages((prev) => {
              const updatedImages = [...prev, ...newImages];
              if (prev.length === 0 && newImages.length > 0) {
                setWidth(newImages[0].originalWidth);
                setHeight(newImages[0].originalHeight);
                setAspectRatio(newImages[0].originalWidth / newImages[0].originalHeight);
              }
              return updatedImages;
            });
          }
        };
        img.src = url;
      });
    },
    [showToast],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      handleFilesSelect(e.dataTransfer.files);
    },
    [handleFilesSelect],
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFilesSelect(e.target.files);
    e.target.value = '';
  };

  const removeImage = (id: string) => {
    const img = images.find((i) => i.id === id);
    if (img) {
      URL.revokeObjectURL(img.originalUrl);
      if (img.processedUrl) URL.revokeObjectURL(img.processedUrl);
    }
    setImages((prev) => prev.filter((i) => i.id !== id));
    if (currentIndex >= images.length - 1) {
      setCurrentIndex(Math.max(0, currentIndex - 1));
    }
  };

  const handleWidthChange = (newWidth: number) => {
    setWidth(newWidth);
    if (aspectLocked) {
      setHeight(Math.round(newWidth / aspectRatio));
    }
  };

  const handleHeightChange = (newHeight: number) => {
    setHeight(newHeight);
    if (aspectLocked) {
      setWidth(Math.round(newHeight * aspectRatio));
    }
  };

  const toggleAspectLock = () => {
    if (!aspectLocked) {
      setAspectRatio(width / height);
    }
    setAspectLocked(!aspectLocked);
  };

  const startCropping = () => {
    setIsCropping(true);
    setCropAspect(undefined);
    if (imageRef.current) {
      const { width: imgWidth, height: imgHeight } = imageRef.current;
      const newCrop = centerCrop(
        makeAspectCrop({ unit: '%', width: 90 }, imgWidth / imgHeight, imgWidth, imgHeight),
        imgWidth,
        imgHeight,
      );
      setCrop(newCrop);
      // Set completedCrop immediately so crop can be applied without moving
      const pixelCrop: PixelCrop = {
        unit: 'px',
        x: (newCrop.x / 100) * imgWidth,
        y: (newCrop.y / 100) * imgHeight,
        width: (newCrop.width / 100) * imgWidth,
        height: (newCrop.height / 100) * imgHeight,
      };
      setCompletedCrop(pixelCrop);
    }
  };

  const setCropAspectRatio = (ratio: number | undefined) => {
    setCropAspect(ratio);
    if (imageRef.current) {
      const { width: imgWidth, height: imgHeight } = imageRef.current;
      const effectiveRatio = ratio || imgWidth / imgHeight;
      const newCrop = centerCrop(
        makeAspectCrop({ unit: '%', width: 80 }, effectiveRatio, imgWidth, imgHeight),
        imgWidth,
        imgHeight,
      );
      setCrop(newCrop);
      // Set completedCrop immediately so crop can be applied without moving
      const pixelCrop: PixelCrop = {
        unit: 'px',
        x: (newCrop.x / 100) * imgWidth,
        y: (newCrop.y / 100) * imgHeight,
        width: (newCrop.width / 100) * imgWidth,
        height: (newCrop.height / 100) * imgHeight,
      };
      setCompletedCrop(pixelCrop);
    }
  };

  const resetCrop = () => {
    if (!currentImage) return;

    // Check if image dimensions differ from pristine (indicates cropping was applied)
    const hasBeenCropped =
      currentImage.originalWidth !== currentImage.pristineWidth ||
      currentImage.originalHeight !== currentImage.pristineHeight;

    if (hasBeenCropped) {
      setIsResetting(true);

      // Revoke the current modified URL (but not pristine URL)
      URL.revokeObjectURL(currentImage.originalUrl);

      // Create a new URL for the restored image from the pristine file
      const restoredUrl = URL.createObjectURL(currentImage.pristineFile);

      // Restore the pristine image
      setImages((prev) =>
        prev.map((i) =>
          i.id === currentImage.id
            ? {
                ...i,
                file: i.pristineFile,
                originalUrl: restoredUrl,
                originalWidth: i.pristineWidth,
                originalHeight: i.pristineHeight,
                // Clear processed result since image changed
                processedUrl: undefined,
                processedSize: undefined,
                processedBlob: undefined,
              }
            : i,
        ),
      );

      // Update dimensions to pristine
      setWidth(currentImage.pristineWidth);
      setHeight(currentImage.pristineHeight);
      setAspectRatio(currentImage.pristineWidth / currentImage.pristineHeight);
      setLastProcessedSettings(null); // Reset since image changed

      // Exit cropping mode
      setIsCropping(false);
      setCrop(undefined);
      setCompletedCrop(undefined);
      setCropAspect(undefined);

      // Small delay for visual feedback, then clear loading state
      setTimeout(() => {
        setIsResetting(false);
        showToast('success', 'Image restored to original!');
      }, 300);
    } else {
      // Image hasn't been cropped yet, just reset the crop selection
      if (imageRef.current) {
        const { width: imgWidth, height: imgHeight } = imageRef.current;
        const newCrop = centerCrop(
          makeAspectCrop({ unit: '%', width: 90 }, imgWidth / imgHeight, imgWidth, imgHeight),
          imgWidth,
          imgHeight,
        );
        setCrop(newCrop);
        setCropAspect(undefined);
        // Set completedCrop immediately
        const pixelCrop: PixelCrop = {
          unit: 'px',
          x: (newCrop.x / 100) * imgWidth,
          y: (newCrop.y / 100) * imgHeight,
          width: (newCrop.width / 100) * imgWidth,
          height: (newCrop.height / 100) * imgHeight,
        };
        setCompletedCrop(pixelCrop);
      }
    }
  };

  const applyCrop = () => {
    if (!completedCrop || !imageRef.current || !currentImage) return;

    setIsApplyingCrop(true);

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      setIsApplyingCrop(false);
      return;
    }

    const scaleX = currentImage.originalWidth / imageRef.current.width;
    const scaleY = currentImage.originalHeight / imageRef.current.height;

    canvas.width = completedCrop.width * scaleX;
    canvas.height = completedCrop.height * scaleY;

    const img = new Image();
    img.onload = () => {
      ctx.drawImage(
        img,
        completedCrop.x * scaleX,
        completedCrop.y * scaleY,
        completedCrop.width * scaleX,
        completedCrop.height * scaleY,
        0,
        0,
        canvas.width,
        canvas.height,
      );

      canvas.toBlob((blob) => {
        if (blob) {
          const newUrl = URL.createObjectURL(blob);
          URL.revokeObjectURL(currentImage.originalUrl);
          setImages((prev) =>
            prev.map((i) =>
              i.id === currentImage.id
                ? {
                    ...i,
                    file: new File([blob], i.file.name, { type: blob.type }),
                    originalUrl: newUrl,
                    originalWidth: canvas.width,
                    originalHeight: canvas.height,
                  }
                : i,
            ),
          );
          setWidth(canvas.width);
          setHeight(canvas.height);
          setAspectRatio(canvas.width / canvas.height);
          setIsCropping(false);
          setCrop(undefined);
          setCompletedCrop(undefined);
          setLastProcessedSettings(null); // Reset since image changed
          showToast('success', 'Crop applied!');
        }
        setIsApplyingCrop(false);
      }, currentImage.file.type);
    };
    img.onerror = () => {
      setIsApplyingCrop(false);
      showToast('error', 'Failed to apply crop');
    };
    img.src = currentImage.originalUrl;
  };

  const cancelCrop = () => {
    setIsCropping(false);
    setCrop(undefined);
    setCompletedCrop(undefined);
  };

  const processImage = async () => {
    if (!currentImage) return;
    setIsProcessing(true);

    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas context not available');

      canvas.width = width;
      canvas.height = height;

      const img = new Image();
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = reject;
        img.src = currentImage.originalUrl;
      });

      ctx.drawImage(img, 0, 0, width, height);

      const mimeType = `image/${outputFormat}`;
      const qualityValue = outputFormat === 'png' ? undefined : quality / 100;

      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob(resolve, mimeType, qualityValue);
      });

      if (!blob) throw new Error('Failed to process image');

      if (currentImage.processedUrl) URL.revokeObjectURL(currentImage.processedUrl);
      const newProcessedUrl = URL.createObjectURL(blob);

      setImages((prev) =>
        prev.map((i) =>
          i.id === currentImage.id
            ? { ...i, processedUrl: newProcessedUrl, processedSize: blob.size, processedBlob: blob }
            : i,
        ),
      );

      // Store the settings used for this processing
      setLastProcessedSettings({
        width,
        height,
        quality,
        format: outputFormat,
        imageId: currentImage.id,
      });

      showToast('success', 'Image processed!');
    } catch (error) {
      console.error('Processing error:', error);
      showToast('error', 'Processing failed');
    } finally {
      setIsProcessing(false);
    }
  };

  const downloadImage = () => {
    if (!currentImage?.processedUrl) return;
    const link = document.createElement('a');
    link.href = currentImage.processedUrl;
    const baseName = currentImage.file.name.replace(/\.[^/.]+$/, '');
    link.download = `${baseName}_processed.${outputFormat}`;
    link.click();
  };

  const resetEditor = () => {
    images.forEach((img) => {
      URL.revokeObjectURL(img.originalUrl);
      if (img.processedUrl) URL.revokeObjectURL(img.processedUrl);
    });
    setImages([]);
    setCurrentIndex(0);
    setIsCropping(false);
    setCrop(undefined);
    setCompletedCrop(undefined);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const savedBytes = currentImage?.processedSize
    ? currentImage.file.size - currentImage.processedSize
    : 0;
  const savedPercent = currentImage?.processedSize
    ? Math.round(
        ((currentImage.file.size - currentImage.processedSize) / currentImage.file.size) * 100,
      )
    : 0;

  // Check if settings have changed since last processing
  const needsProcessing =
    !lastProcessedSettings ||
    lastProcessedSettings.width !== width ||
    lastProcessedSettings.height !== height ||
    lastProcessedSettings.quality !== quality ||
    lastProcessedSettings.format !== outputFormat ||
    lastProcessedSettings.imageId !== currentImage?.id;

  // Check if image has been cropped from its original state
  const hasBeenCropped = currentImage
    ? currentImage.originalWidth !== currentImage.pristineWidth ||
      currentImage.originalHeight !== currentImage.pristineHeight
    : false;

  return {
    // Refs
    fileInputRef,
    imageRef,
    // Images state
    images,
    currentIndex,
    setCurrentIndex,
    currentImage,
    isDragging,
    setIsDragging,
    // Editor settings
    width,
    height,
    aspectLocked,
    quality,
    setQuality,
    outputFormat,
    setOutputFormat,
    // Crop state
    isCropping,
    crop,
    setCrop,
    completedCrop,
    setCompletedCrop,
    cropAspect,
    isApplyingCrop,
    isResetting,
    hasBeenCropped,
    // Processing
    isProcessing,
    needsProcessing,
    // Computed values
    savedBytes,
    savedPercent,
    // Handlers
    handleFilesSelect,
    handleDrop,
    handleInputChange,
    removeImage,
    handleWidthChange,
    handleHeightChange,
    toggleAspectLock,
    startCropping,
    setCropAspectRatio,
    resetCrop,
    applyCrop,
    cancelCrop,
    processImage,
    downloadImage,
    resetEditor,
    formatFileSize,
  };
};
